import { useMemo, useState } from 'react';
import { ChatDocEditor } from '@odo/shared-ui';
import {
    MAX_QUICK_REPLY_CATEGORY_LENGTH,
    MAX_QUICK_REPLY_CODE_LENGTH,
    MAX_QUICK_REPLY_TITLE_LENGTH,
    type QuickReplyChatType
} from '../../../services/conversationQuickReplies';
import { ModalField, MODAL_INPUT_CLASSES } from '../../shared/ModalField';
import { SearchableSelect } from '../../shared/SearchableSelect';
import { QUICK_REPLY_TOKENS } from '../chat/fillQuickReplyTokens';
import {
    isBodyTooLong,
    isCodeValid,
    isSortOrderValid,
    type QuickReplyFormValues
} from './quickReplyForm';
import { THREAD_OPTIONS } from './threadOptions';

const TOKEN_HINT = QUICK_REPLY_TOKENS.map((token) => `{${token}}`).join(', ');
const NEW_CATEGORY = '__new__';
const SELECT_TRIGGER_CLASSES =
    'h-[38px] rounded-md border border-gray-300 bg-white px-3 text-sm shadow-sm hover:border-gray-400 aria-expanded:border-indigo-500 transition-colors duration-200';

type FieldChange = <K extends keyof QuickReplyFormValues>(
    field: K,
    value: QuickReplyFormValues[K]
) => void;

interface QuickReplyFormFieldsProps {
    values: QuickReplyFormValues;
    categories: string[];
    disabled: boolean;
    onChange: FieldChange;
}

export function QuickReplyFormFields({
    values,
    categories,
    disabled,
    onChange
}: QuickReplyFormFieldsProps) {
    const codeInvalid = values.code.trim() !== '' && !isCodeValid(values.code);

    return (
        <div className="grid grid-cols-2 gap-4">
            <ModalField
                htmlFor="quick-reply-code"
                label="Code"
                required
                hintTone={codeInvalid ? 'error' : 'muted'}
                hint="Uppercase letters, digits and hyphens, e.g. SCAN-RETAKE."
            >
                <input
                    id="quick-reply-code"
                    type="text"
                    value={values.code}
                    autoFocus
                    disabled={disabled}
                    maxLength={MAX_QUICK_REPLY_CODE_LENGTH}
                    onChange={(e) => onChange('code', e.target.value.toUpperCase())}
                    className={`${MODAL_INPUT_CLASSES} font-mono`}
                />
            </ModalField>
            <ModalField htmlFor="quick-reply-thread" label="Thread" required>
                <SearchableSelect
                    mode="single"
                    id="quick-reply-thread"
                    label="Thread"
                    value={values.chatType}
                    options={THREAD_OPTIONS}
                    placeholder="Select a thread"
                    searchPlaceholder="Search threads"
                    disabled={disabled}
                    triggerClassName={SELECT_TRIGGER_CLASSES}
                    onChange={(chatType) => onChange('chatType', chatType as QuickReplyChatType)}
                />
            </ModalField>
            <div className="col-span-2">
                <ModalField htmlFor="quick-reply-title" label="Title" required>
                    <input
                        id="quick-reply-title"
                        type="text"
                        value={values.title}
                        disabled={disabled}
                        maxLength={MAX_QUICK_REPLY_TITLE_LENGTH}
                        onChange={(e) => onChange('title', e.target.value)}
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>
            </div>
            <CategoryField
                categories={categories}
                value={values.category}
                disabled={disabled}
                onChange={(category) => onChange('category', category)}
            />
            <ModalField
                htmlFor="quick-reply-sort-order"
                label="Sort order"
                hintTone={isSortOrderValid(values.sortOrder) ? 'muted' : 'error'}
                hint="Lower numbers list first within the category."
            >
                <input
                    id="quick-reply-sort-order"
                    type="text"
                    inputMode="numeric"
                    value={values.sortOrder}
                    disabled={disabled}
                    onChange={(e) => onChange('sortOrder', e.target.value)}
                    className={MODAL_INPUT_CLASSES}
                />
            </ModalField>
            <div className="col-span-2">
                <ModalField
                    htmlFor="quick-reply-body"
                    label="Message"
                    required
                    hintTone={isBodyTooLong(values.bodyDoc) ? 'error' : 'muted'}
                    hint={
                        isBodyTooLong(values.bodyDoc)
                            ? 'This message is too long to save. Shorten it.'
                            : `Auto-filled: ${TOKEN_HINT}. Put anything staff must fill in square brackets, e.g. [aligner #] or [date]; sending warns while one is left.`
                    }
                >
                    <ChatDocEditor
                        id="quick-reply-body"
                        ariaLabel="Message"
                        initialDoc={values.bodyDoc}
                        disabled={disabled}
                        onChange={(bodyDoc) => onChange('bodyDoc', bodyDoc)}
                    />
                </ModalField>
            </div>
        </div>
    );
}

interface CategoryFieldProps {
    categories: string[];
    value: string;
    disabled: boolean;
    onChange: (category: string) => void;
}

// Existing categories, plus a way out to name a new one.
function CategoryField({ categories, value, disabled, onChange }: CategoryFieldProps) {
    const [isNew, setIsNew] = useState(false);
    const options = useMemo(
        () => [
            ...categories.map((category) => ({ value: category, label: category })),
            { value: NEW_CATEGORY, label: 'New category…', separatedAbove: categories.length > 0 }
        ],
        [categories]
    );

    const select = (next: string) => {
        const creating = next === NEW_CATEGORY;
        setIsNew(creating);
        onChange(creating ? '' : next);
    };

    return (
        <ModalField htmlFor="quick-reply-category" label="Category" required>
            <SearchableSelect
                mode="single"
                id="quick-reply-category"
                label="Category"
                value={isNew ? NEW_CATEGORY : value || null}
                options={options}
                placeholder="Select a category"
                searchPlaceholder="Search categories"
                disabled={disabled}
                triggerClassName={SELECT_TRIGGER_CLASSES}
                onChange={select}
            />
            {isNew && (
                <input
                    type="text"
                    aria-label="New category"
                    placeholder="Name the new category"
                    autoFocus
                    value={value}
                    disabled={disabled}
                    maxLength={MAX_QUICK_REPLY_CATEGORY_LENGTH}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${MODAL_INPUT_CLASSES} mt-2`}
                />
            )}
        </ModalField>
    );
}
